import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Clock, 
    Activity,
    CheckCircle,
    User,
    Plus,
    Edit2,
    Calendar,
    Filter,
    X,
    ChevronDown,
    ChevronUp,
    TrendingUp
} from 'react-feather';
import { projectApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tooltip } from '../components/ui/Tooltip';
import { FeatherArrowLeft } from '../components/ui/icons';
import ActivitySummary from './components/ActivitySummary';
import './ProjectActivityHistory.css';

const ProjectActivityHistory = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [activities, setActivities] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Date range state - default to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    const [startDate, setStartDate] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
    
    // Filter states
    const [selectedActivityTypes, setSelectedActivityTypes] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedFeatureTagId, setSelectedFeatureTagId] = useState('');
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [summaryCollapsed, setSummaryCollapsed] = useState(false);

    useEffect(() => {
        if (projectId) {
            fetchProject();
            fetchActivityHistory();
        }
    }, [projectId, startDate, endDate]);

    const fetchProject = async () => {
        try {
            const data = await projectApi.get(projectId);
            setProject(data);
        } catch (err) {
            console.error('Error fetching project:', err);
        }
    };

    const fetchActivityHistory = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await projectApi.getActivityHistory(projectId, startDate, endDate);
            // Handle both old format (array) and new format (object with activities and summary)
            if (Array.isArray(response)) {
                setActivities(response);
                setSummary(null);
            } else {
                setActivities(response.activities || []);
                setSummary(response.summary || null);
            }
        } catch (err) {
            console.error('Error fetching activity history:', err);
            setError('Failed to load activity history');
        } finally {
            setLoading(false);
        }
    };

    const getActivityIcon = (type) => {
        const iconProps = { size: 18 };
        switch (type) {
            case 'work_logged':
                return <Clock {...iconProps} />;
            case 'task_created':
                return <Plus {...iconProps} />;
            case 'task_status_changed':
                return <CheckCircle {...iconProps} />;
            case 'task_assigned':
                return <User {...iconProps} />;
            case 'task_updated':
                return <Edit2 {...iconProps} />;
            default:
                return <Activity {...iconProps} />;
        }
    };

    const getActivityColor = (type) => {
        switch (type) {
            case 'work_logged':
                return 'info';
            case 'task_created':
                return 'success';
            case 'task_status_changed':
                return 'warning';
            case 'task_assigned':
                return 'info';
            case 'task_updated':
                return 'neutral';
            default:
                return 'neutral';
        }
    };

    const getActivityTypeLabel = (type) => {
        switch (type) {
            case 'work_logged':
                return 'Work Logged';
            case 'task_created':
                return 'Task Created';
            case 'task_status_changed':
                return 'Status Changed';
            case 'task_assigned':
                return 'Task Assigned';
            case 'task_updated':
                return 'Task Updated';
            default:
                return 'Activity';
        }
    };

    const getActivityTypeTooltip = (type) => {
        switch (type) {
            case 'work_logged':
                return 'Time was logged on this task';
            case 'task_created':
                return 'A new task was created';
            case 'task_status_changed':
                return 'The task status was changed';
            case 'task_assigned':
                return 'The task assignment was changed';
            case 'task_updated':
                return 'The task was updated';
            default:
                return 'An activity occurred';
        }
    };

    const getStatusVariant = (status) => {
        if (!status) return 'neutral';
        switch (status.toLowerCase()) {
            case 'completed':
            case 'released':
                return 'success';
            case 'in-progress':
                return 'warning';
            case 'open':
                return 'neutral';
            default:
                return 'neutral';
        }
    };

    const formatActivityMessage = (activity) => {
        const { type, user_name, task_title, task_display_id, details } = activity;
        const taskRef = task_display_id ? `#${task_display_id}` : 'task';

        switch (type) {
            case 'work_logged':
                const hours = activity.hours || 0;
                return {
                    action: `${user_name} logged ${hours}h`,
                    taskRef: taskRef,
                    taskTitle: task_title || 'Unknown Task'
                };
            
            case 'task_created':
                return {
                    action: `${user_name} created`,
                    taskRef: taskRef,
                    taskTitle: task_title || 'Unknown Task'
                };
            
            case 'task_status_changed':
                const oldStatus = details?.old_value || 'unknown';
                const newStatus = details?.new_value || 'unknown';
                const statusChange = oldStatus !== newStatus 
                    ? `from "${oldStatus}" to "${newStatus}"`
                    : `to "${newStatus}"`;
                return {
                    action: `${user_name} changed status`,
                    statusChange: statusChange,
                    taskRef: taskRef,
                    taskTitle: task_title || 'Unknown Task'
                };
            
            case 'task_assigned':
                const oldAssignee = details?.old_value || 'Unassigned';
                const newAssignee = details?.new_value || 'Unassigned';
                if (oldAssignee === 'Unassigned' || !oldAssignee) {
                    return {
                        action: `${user_name} assigned`,
                        assignee: `to "${newAssignee}"`,
                        taskRef: taskRef,
                        taskTitle: task_title || 'Unknown Task'
                    };
                }
                return {
                    action: `${user_name} reassigned`,
                    assignee: `from "${oldAssignee}" to "${newAssignee}"`,
                    taskRef: taskRef,
                    taskTitle: task_title || 'Unknown Task'
                };
            
            case 'task_updated':
                return {
                    action: `${user_name} updated`,
                    taskRef: taskRef,
                    taskTitle: task_title || 'Unknown Task'
                };
            
            default:
                return {
                    action: `${user_name} performed an action`,
                    taskRef: taskRef,
                    taskTitle: task_title || 'Unknown Task'
                };
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    // Filter activities
    const filteredActivities = activities.filter(activity => {
        // Filter by activity type
        if (selectedActivityTypes.length > 0 && !selectedActivityTypes.includes(activity.type)) {
            return false;
        }
        
        // Filter by user
        if (selectedUserId && activity.user_id !== selectedUserId) {
            return false;
        }
        
        // Filter by feature tag
        if (selectedFeatureTagId && activity.feature_tag?.id !== selectedFeatureTagId) {
            return false;
        }
        
        return true;
    });

    // Get unique values for filters
    const userMap = new Map();
    activities.forEach(a => {
        if (a.user_id && !userMap.has(a.user_id)) {
            userMap.set(a.user_id, { id: a.user_id, name: a.user_name, email: a.user_email });
        }
    });
    const uniqueUsers = Array.from(userMap.values());
    
    const featureTagMap = new Map();
    activities.forEach(a => {
        if (a.feature_tag && !featureTagMap.has(a.feature_tag.id)) {
            featureTagMap.set(a.feature_tag.id, a.feature_tag);
        }
    });
    const uniqueFeatureTags = Array.from(featureTagMap.values());
    
    const activityTypes = ['work_logged', 'task_created', 'task_status_changed', 'task_assigned', 'task_updated'];

    // Group activities by date
    const groupedActivities = filteredActivities.reduce((groups, activity) => {
        const date = activity.date || activity.timestamp?.split('T')[0] || 'Unknown';
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(activity);
        return groups;
    }, {});

    const sortedDates = Object.keys(groupedActivities).sort((a, b) => {
        // Handle 'Unknown' dates - sort them to the end
        if (a === 'Unknown' && b === 'Unknown') {
            return 0;
        }
        if (a === 'Unknown') {
            return 1; // Sort 'Unknown' after valid dates
        }
        if (b === 'Unknown') {
            return -1; // Sort 'Unknown' after valid dates
        }
        // Normal date comparison (descending order - most recent first)
        return new Date(b) - new Date(a);
    });

    const handleDateChange = () => {
        fetchActivityHistory();
    };

    const handleActivityTypeToggle = (type) => {
        setSelectedActivityTypes(prev => 
            prev.includes(type) 
                ? prev.filter(t => t !== type)
                : [...prev, type]
        );
    };

    const clearFilters = () => {
        setSelectedActivityTypes([]);
        setSelectedUserId('');
        setSelectedFeatureTagId('');
    };

    const hasActiveFilters = selectedActivityTypes.length > 0 || selectedUserId || selectedFeatureTagId;

    // Get active filter labels for display
    const getActiveFilterLabels = () => {
        const labels = [];
        if (selectedActivityTypes.length > 0) {
            labels.push(`${selectedActivityTypes.length} type${selectedActivityTypes.length > 1 ? 's' : ''}`);
        }
        if (selectedUserId) {
            const user = uniqueUsers.find(u => u.id === selectedUserId);
            labels.push(user?.name || user?.email || 'User');
        }
        if (selectedFeatureTagId) {
            const tag = uniqueFeatureTags.find(t => t.id === selectedFeatureTagId);
            labels.push(tag?.name || 'Feature');
        }
        return labels;
    };

    return (
        <div className="project-activity-history">
            
            <div className="activity-history-header">
                <div className="activity-history-header-top">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherArrowLeft />}
                        onClick={() => navigate(`/projects/${projectId}`)}
                        className="back-button"
                    >
                        Back to Project
                    </Button>
                </div>
                <div className="activity-history-header-content">
                    <div>
                        <h1 className="activity-history-title">
                            Activity History
                            {project && <span className="project-name"> - {project.name}</span>}
                        </h1>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="activity-filters-section">
                <div 
                    className="activity-filters-header" 
                    onClick={() => setFiltersCollapsed(!filtersCollapsed)}
                    style={{ cursor: 'pointer' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, flexWrap: 'wrap' }}>
                        <h3 className="activity-filters-title">
                            <Filter size={16} />
                            Filters
                        </h3>
                        {filtersCollapsed && hasActiveFilters && (
                            <div className="active-filters-badges">
                                {getActiveFilterLabels().map((label, index) => (
                                    <span key={index} className="filter-badge">
                                        {label}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {hasActiveFilters && !filtersCollapsed && (
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearFilters();
                                }} 
                                className="clear-filters-button"
                                title="Clear all filters"
                            >
                                <X size={14} />
                                Clear
                            </button>
                        )}
                        <button 
                            className="filters-toggle-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                setFiltersCollapsed(!filtersCollapsed);
                            }}
                            aria-label={filtersCollapsed ? "Expand filters" : "Collapse filters"}
                        >
                            {filtersCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                        </button>
                    </div>
                </div>
                <div className={`activity-filters-content ${filtersCollapsed ? 'collapsed' : ''}`}>
                    {/* Row 1: User, Feature Tag, and Activity Type */}
                    <div className="filters-row filters-row-single">
                        <div className="filter-group">
                            <label className="filter-label">
                                <span className="filter-label-text">User</span>
                            </label>
                            <div className="filter-select-wrapper">
                                <select
                                    value={selectedUserId}
                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="">All Users</option>
                                    {uniqueUsers.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.name || user.email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                <span className="filter-label-text">Feature Tag</span>
                            </label>
                            <div className="filter-select-wrapper">
                                <select
                                    value={selectedFeatureTagId}
                                    onChange={(e) => setSelectedFeatureTagId(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="">All Features</option>
                                    {uniqueFeatureTags.map(tag => (
                                        <option key={tag.id} value={tag.id}>
                                            {tag.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                <span className="filter-label-text">Activity Type</span>
                            </label>
                            <div className="filter-checkboxes">
                                {activityTypes.map(type => (
                                    <label key={type} className="filter-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedActivityTypes.includes(type)}
                                            onChange={() => handleActivityTypeToggle(type)}
                                        />
                                        <span>{getActivityTypeLabel(type)}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    {/* Row 2: Date Range */}
                    <div className="filters-row filters-row-date">
                        <div className="filter-group">
                            <label className="filter-label">
                                <span className="filter-label-text">Start Date</span>
                            </label>
                            <input
                                id="start-date"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                onBlur={handleDateChange}
                                max={endDate}
                                className="filter-date-input"
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                <span className="filter-label-text">End Date</span>
                            </label>
                            <input
                                id="end-date"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                onBlur={handleDateChange}
                                min={startDate}
                                max={today.toISOString().split('T')[0]}
                                className="filter-date-input"
                            />
                        </div>
                        <div className="filter-group filter-group-button">
                            <label className="filter-label" style={{ visibility: 'hidden' }}>
                                <span className="filter-label-text">Apply</span>
                            </label>
                            <Button
                                variant="brand-primary"
                                onClick={handleDateChange}
                                className="apply-date-button"
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Statistics */}
            {summary && (
                <div className="activity-summary-section">
                    <div 
                        className="activity-summary-header" 
                        onClick={() => setSummaryCollapsed(!summaryCollapsed)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                            <h3 className="activity-summary-title-header">
                                <TrendingUp size={16} />
                                Summary Statistics
                            </h3>
                        </div>
                        <button 
                            className="summary-toggle-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSummaryCollapsed(!summaryCollapsed);
                            }}
                            aria-label={summaryCollapsed ? "Expand summary" : "Collapse summary"}
                        >
                            {summaryCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                        </button>
                    </div>
                    <div className={`activity-summary-content ${summaryCollapsed ? 'collapsed' : ''}`}>
                        <ActivitySummary summary={summary} />
                    </div>
                </div>
            )}

            

            {/* Loading State */}
            {loading && (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading activity history...</p>
                </div>
            )}

            {/* Error State */}
            {error && !loading && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredActivities.length === 0 && activities.length > 0 && (
                <div className="empty-state">
                    <Filter size={48} className="empty-icon" />
                    <h4>No Activities Match Filters</h4>
                    <p>Try adjusting your filters to see more activities.</p>
                    <button onClick={clearFilters} className="clear-filters-link">
                        Clear all filters
                    </button>
                </div>
            )}

            {!loading && !error && activities.length === 0 && (
                <div className="empty-state">
                    <Activity size={48} className="empty-icon" />
                    <h4>No Activity Found</h4>
                    <p>No activities were found for the selected date range.</p>
                </div>
            )}

            {/* Activities Timeline */}
            {!loading && !error && filteredActivities.length > 0 && (
                <div className="activities-timeline">
                    {sortedDates.map(date => (
                        <div key={date} className="activity-date-group">
                            <div className="date-header">
                                <h3>{date === 'Unknown' 
                                    ? 'Unknown Date'
                                    : new Date(date).toLocaleDateString('en-US', { 
                                        weekday: 'long',
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                    })
                                }</h3>
                            </div>
                            <div className="activities-list">
                                {groupedActivities[date].map((activity, index) => {
                                    const icon = getActivityIcon(activity.type);
                                    const color = getActivityColor(activity.type);
                                    const message = formatActivityMessage(activity);
                                    const timestamp = formatTimestamp(activity.timestamp);
                                    const typeLabel = getActivityTypeLabel(activity.type);
                                    const tooltipText = getActivityTypeTooltip(activity.type);

                                    return (
                                        <div key={index} className="activity-item">
                                            <Tooltip
                                                title={typeLabel}
                                                description={tooltipText}
                                                placement="right"
                                            >
                                                <div className="activity-icon">
                                                    <div className={`icon-wrapper ${color}`}>
                                                        {icon}
                                                    </div>
                                                </div>
                                            </Tooltip>
                                            <div className="activity-content">
                                                <div className="activity-header">
                                                    <div className="activity-message">
                                                        <div className="activity-message-action">
                                                            {message.action}
                                                        </div>
                                                        <div className="activity-message-task">
                                                            <span className="activity-task-ref">{message.taskRef}</span>
                                                            <span className="activity-task-title">{message.taskTitle}</span>
                                                        </div>
                                                        {message.statusChange && (
                                                            <div className="activity-message-detail">
                                                                Status {message.statusChange}
                                                            </div>
                                                        )}
                                                        {message.assignee && (
                                                            <div className="activity-message-detail">
                                                                {message.assignee}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Badge variant={color} className="activity-type-badge">
                                                        {typeLabel}
                                                    </Badge>
                                                </div>
                                                <div className="activity-badges">
                                                    {activity.feature_tag && (
                                                        <Badge variant="neutral" className="activity-feature-badge">
                                                            <span 
                                                                className="feature-tag-color-indicator"
                                                                style={{ backgroundColor: activity.feature_tag.color || '#6B7280' }}
                                                            />
                                                            {activity.feature_tag.name}
                                                        </Badge>
                                                    )}
                                                    {activity.task_status && (
                                                        <Badge variant={getStatusVariant(activity.task_status)}>
                                                            {activity.task_status.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                                        </Badge>
                                                    )}
                                                    {activity.task_type && activity.task_type !== 'Task' && (
                                                        <Badge variant="neutral">
                                                            {activity.task_type}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {activity.type === 'work_logged' && (
                                                    <div className="activity-work-details">
                                                        {activity.hours > 0 && (
                                                            <div className="activity-hours">
                                                                <Clock size={14} />
                                                                <span>{activity.hours}h logged</span>
                                                            </div>
                                                        )}
                                                        {activity.details?.task_total_hours !== undefined && (
                                                            <div className="activity-total-hours">
                                                                <span className="activity-detail-label">Total:</span>
                                                                <span className="activity-detail-value">{activity.details.task_total_hours}h</span>
                                                            </div>
                                                        )}
                                                        {activity.details?.task_first_started && (
                                                            <div className="activity-first-started">
                                                                <span className="activity-detail-label">Started:</span>
                                                                <span className="activity-detail-value">
                                                                    {new Date(activity.details.task_first_started).toLocaleDateString('en-US', {
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                        year: 'numeric'
                                                                    })}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {activity.details?.attempt_number && (
                                                            <div className="activity-attempt">
                                                                <span className="activity-detail-label">Attempt:</span>
                                                                <span className="activity-detail-value">#{activity.details.attempt_number}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {activity.type !== 'work_logged' && activity.hours > 0 && (
                                                    <div className="activity-hours">
                                                        <Clock size={14} />
                                                        <span>{activity.hours}h logged</span>
                                                    </div>
                                                )}
                                                {activity.notes && (
                                                    <div className="activity-notes">
                                                        <span className="activity-notes-label">Notes:</span>
                                                        <span className="activity-notes-text">{activity.notes}</span>
                                                    </div>
                                                )}
                                                <div className="activity-meta">
                                                    <span className="activity-time">
                                                        <Clock size={14} />
                                                        {timestamp}
                                                    </span>
                                                    {activity.user_email && (
                                                        <span className="activity-user">
                                                            <User size={14} />
                                                            {activity.user_email}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProjectActivityHistory;
